import { Link } from "@tanstack/react-router";
import { BellIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/shared/lib/utils";
import type { AdminNotificationItem } from "../../domain/types";
import { useAdminNotificationsQuery } from "../api/queries";
import { useNotificationSound } from "../hooks/use-notification-sound";
import {
  formatNotificationBadge,
  NOTIFICATION_LINKS,
} from "../lib/notification-links";

function NotificationRow({ item }: { item: AdminNotificationItem }) {
  const link = NOTIFICATION_LINKS[item.id];
  const rowClassName =
    "hover:bg-muted flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors";

  const content = (
    <>
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-bold text-primary text-xs tabular-nums">
        {formatNotificationBadge(item.count)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-sm leading-snug">
          {item.title}
        </span>
        <span className="mt-0.5 block text-muted-foreground text-xs">
          {item.count.toLocaleString("lo-LA")} ລາຍການ
        </span>
      </span>
    </>
  );

  if ("search" in link) {
    return (
      <Link to={link.to} search={link.search} className={rowClassName}>
        {content}
      </Link>
    );
  }

  return (
    <Link to={link.to} className={rowClassName}>
      {content}
    </Link>
  );
}

export function NotificationBell() {
  const { data, isLoading } = useAdminNotificationsQuery();
  const totalCount = data?.totalCount ?? 0;
  const items = data?.items ?? [];
  useNotificationSound(data ? items : undefined);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-9 rounded-full"
          aria-label="ແຈ້ງເຕືອນ"
        >
          <BellIcon className="size-[1.15rem]" />
          {totalCount > 0 ? (
            <Badge
              variant="destructive"
              className={cn(
                "absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1 font-bold text-[10px] tabular-nums",
              )}
            >
              {formatNotificationBadge(totalCount)}
            </Badge>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-border/60 border-b px-4 py-3">
          <p className="font-semibold text-sm">ແຈ້ງເຕືອນ</p>
          <p className="mt-0.5 text-muted-foreground text-xs">
            {isLoading
              ? "ກຳລັງໂຫຼດ..."
              : totalCount > 0
                ? `${totalCount.toLocaleString("lo-LA")} ລາຍການລໍຖ້າດຳເນີນການ`
                : "ບໍ່ມີລາຍການໃໝ່"}
          </p>
        </div>

        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-muted-foreground text-sm">
            ບໍ່ມີແຈ້ງເຕືອນໃໝ່
          </div>
        ) : (
          <div className="max-h-80 space-y-0.5 overflow-y-auto p-2">
            {items.map((item) => (
              <NotificationRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
